'use client'

import * as React from 'react'
import type { InstantSearchProps } from 'react-instantsearch'
import { InstantSearch, Configure } from 'react-instantsearch'
import type { UiState } from 'instantsearch.js'
import { useSearchParams } from 'next/navigation'
import { algoliaContentIndex } from 'algolia/algolia-content'
import { defaultSearchClient } from 'algolia/algolia'
import { InsightsMiddleware } from 'algolia/insights-middleware'
import { useRouter, usePathname } from '@repo/nextjs-utils/navigation'

import { useContentSearchCategoryQuery } from '@web/gen/content'
import type { ContentLanguage } from '@repo/graphql-types'
import { getSearchState, searchStateToQuery } from '@web/components/content-api/search-page/utils'
import { buildAlgoliaColorFilter } from '../utils/build-algolia-color-filter'
import { mapCategoryPageTypeToCategoryDetails } from '@web/components/content-api/config'

export const SearchPageContext = React.createContext<{
  color: string | null
  updateColor: (newColor: string | null) => void
  contentLanguage: ContentLanguage
  categoryName: string
  expanded: string[] | null
}>(null!)

export function useSearchPageContext() {
  const context = React.useContext(SearchPageContext)

  if (!context) {
    throw new Error('useSearchPageContext must be used within a SearchPageContext')
  }

  return context
}

const useStockPhotoCategories = (contentLanguage: ContentLanguage, isStockPhotos: boolean) => {
  const { data } = useContentSearchCategoryQuery(
    {
      name: '',
      parentId: mapCategoryPageTypeToCategoryDetails.stockphotos.parentId,
      language: contentLanguage,
    },
    { enabled: isStockPhotos },
  )

  return {
    data,
    categories: Object.fromEntries(
      data?.contentSearchCategory?.map(category => [category.id, category]) ?? [],
    ),
  }
}

const useColorUrlState = () => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [color, setColor] = React.useState(() => {
    const value = searchParams?.get('color')
    return value ? decodeURIComponent(value) : null
  })

  React.useEffect(() => {
    const searchParamsString = searchParams.toString()
    const queryParams = new URLSearchParams(searchParamsString)

    if (color) {
      queryParams.set('color', color)
    } else {
      queryParams.delete('color')
    }

    const query = queryParams.toString()
    if (query.length > 0 && searchParamsString !== query) {
      if (typeof window === 'undefined') {
        router.push(`${pathname}?${query}`, { scroll: false })
      } else {
        window.history.pushState({}, '', `?${query}`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color])

  return { color, setColor, searchParams, pathname, router }
}

const useContentSearchStateChange = ({
  authorId,
  categories,
  color,
  contentLanguage,
  expanded,
  isStockPhotos,
  pathname,
  router,
}: {
  authorId: string
  categories: ReturnType<typeof useStockPhotoCategories>['categories']
  color: string | null
  contentLanguage: ContentLanguage
  expanded: string | null | undefined
  isStockPhotos: boolean
  pathname: ReturnType<typeof usePathname>
  router: ReturnType<typeof useRouter>
}) =>
  React.useCallback(
    ({
      uiState,
      setUiState,
    }: {
      uiState: UiState
      setUiState: (uiState: UiState | ((previousUiState: UiState) => UiState)) => void
    }) => {
      const query = searchStateToQuery({
        searchState: {
          uiState,
          other: { expanded: expanded || '', color },
        },
        lang: contentLanguage,
        categories,
      })
      const newState = getSearchState({
        url: query,
        lang: contentLanguage,
        authorId,
        isStockPhotos,
        categories,
      })

      setUiState(newState.uiState)
      if (typeof window === 'undefined') {
        router.push(`${pathname}?${query}`, { scroll: false })
      } else {
        window.history.pushState({}, '', `?${query}`)
      }
    },
    [authorId, categories, color, contentLanguage, expanded, isStockPhotos, pathname, router],
  )

export const ContentSearchPageInstantSearchProvider: React.FC<
  Partial<InstantSearchProps> & {
    authorId: string
    contentLanguage: ContentLanguage
    hitsPerPage?: number
    categoryName: string
  }
> = ({ children, authorId, contentLanguage, hitsPerPage = 50, categoryName }) => {
  const isStockPhotos =
    categoryName === mapCategoryPageTypeToCategoryDetails.stockphotos.categoryName
  const { data, categories } = useStockPhotoCategories(contentLanguage, isStockPhotos)
  const { color, setColor, searchParams, pathname, router } = useColorUrlState()
  const expanded = searchParams?.get('expanded')

  const searchState = getSearchState({
    url: searchParams?.toString() ?? '',
    lang: contentLanguage,
    authorId,
    isStockPhotos,
    categories,
  })

  const handleStateChange = useContentSearchStateChange({
    authorId,
    categories,
    color,
    contentLanguage,
    expanded,
    isStockPhotos,
    pathname,
    router,
  })

  const colorFilters = color ? buildAlgoliaColorFilter(color) : ''

  if (isStockPhotos && !data) {
    return null
  }

  return (
    <InstantSearch
      insights={false}
      initialUiState={searchState.uiState}
      onStateChange={handleStateChange}
      searchClient={defaultSearchClient}
      indexName={algoliaContentIndex}
    >
      <Configure
        hitsPerPage={hitsPerPage}
        filters={`authorId:${authorId}${color ? ` AND ${colorFilters}` : ''}`}
      />
      <SearchPageContext.Provider
        value={{
          color: color,
          updateColor: setColor,
          contentLanguage,
          categoryName,
          expanded: expanded ? expanded.split(',') : null,
        }}
      >
        {children}
      </SearchPageContext.Provider>
      <InsightsMiddleware />
    </InstantSearch>
  )
}
